/**
 * セル番号と列・行の相互変換。
 *
 * 盤面は `C` 列 × `R` 行のグリッドで、セルは `cell = row × C + col` の通し番号で表す。
 * 盤面の外に領域を持たないため、ここでの変換はすべて盤内座標に閉じている。
 *
 * 根拠: spec/04a-puzzle-core.md §1.1。
 */

import type { CellIndex } from './types';

/** セル `c` の列番号（0 始まり）。 */
export function col(c: CellIndex, C: number): number {
  return c % C;
}

/** セル `c` の行番号（0 始まり）。 */
export function row(c: CellIndex, C: number): number {
  return Math.floor(c / C);
}

/** 列・行からセル番号を作る。`col` / `row` の逆変換。 */
export function cellOf(col: number, row: number, C: number): CellIndex {
  return row * C + col;
}

/**
 * 列・行が盤内に収まるか。
 *
 * 列だけを見た剰余判定では、行をまたいだ折り返し（`col = C` が次の行の先頭になる）を
 * 見逃す。押しのけの盤外判定はこの関数に集約する。
 */
export function inBoard(col: number, row: number, C: number, R: number): boolean {
  return col >= 0 && col < C && row >= 0 && row < R;
}

/**
 * 盤面上で上下左右に隣接するセル対を、各対ちょうど 1 回ずつ列挙する。
 *
 * 右隣と下隣だけを見ることで、`(a,b)` と `(b,a)` の重複を避ける。列挙されるのは
 * `2CR − C − R` 対で、これはセル数 `N = CR` に対して線形である。結合の算出が
 * `O(N)` で済む根拠がここにある（`04a` §2.4）。
 */
export function* adjacentCellPairs(C: number, R: number): Generator<[CellIndex, CellIndex]> {
  const N = C * R;
  for (let cell = 0; cell < N; cell++) {
    if (col(cell, C) < C - 1) yield [cell, cell + 1];
    if (row(cell, C) < R - 1) yield [cell, cell + C];
  }
}
